import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  Box,
  Stack,
  Grid,
  CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomSelect } from '../../../../components/CustomSelect';
import { CustomButton } from '../../../../components/CustomButton';
import { ImageUploader } from '../../../../components/ImageUploader';
import { getCompanyBranches, getDepartmentsOfBranch } from '../../../../store/company/actions';
import { getListRoles } from '../../../../store/role/actions';
import { getCurrentUser, updateUser, uploadUserLogo } from '../../../../store/user/actions';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';

export const EditProfile = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [sleep, setSleep] = useState(false);

  const userState = useSelector((state) => state.currentUser);
  const { userData: userInfo, fetching } = userState;
  const [userAvatar, setUserAvatar] = useState(userInfo.avatar);

  const roleListState = useSelector((state) => state.roleList);
  const { items: roleOptions, fetching: fetchingRoleOptions } = roleListState;

  const companyBranchesState = useSelector((state) => state.company);
  const { branches: branchesOptions, fetching: fetchingBranchesOptions } = companyBranchesState;

  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const isAbleToUpdateRole = !visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUserRoles.value);

  useEffect(() => {
    dispatch(getListRoles());
    dispatch(getCompanyBranches());
  }, [dispatch]);

  useEffect(() => {
    if (userInfo.branch_id && branchesOptions.length) {
      dispatch(getDepartmentsOfBranch(userInfo.branch_id));
    }
  }, // eslint-disable-next-line
    [dispatch, userInfo.branch_id]);

  const { handleSubmit, values, handleChange, setFieldValue, dirty, isValid, errors } = useFormik({
    validationSchema: yup.object({
      firstName: yup.string().max(256, 'Максимум 256 символов').required('Обязательное поле'),
      lastName: yup.string().max(256, 'Максимум 256 символов').required('Обязательное поле'),
      middleName: yup.string().max(256, 'Максимум 256 символов').nullable(),
      phone: yup.string().test('len', 'Must be exactly 15 characters', (val) => {
        return val?.length === 15 || !val;
      }).nullable(),
      /* email: yup.string().test(
        'email',
        'Некорректный email',
          val => val ?
            REGULAR_EXPRESSIONS.email_validation_pattern.test(val)
            : true
      ).required('Обязательное поле') */
    }),
    initialValues: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      middleName: userInfo.middleName,
      email: userInfo.email,
      phone: userInfo.phone,
      avatar: userInfo.avatar,
      role: roleOptions.find((role) => role.id === Number(userInfo.role?.id)),
      branch: userInfo?.branch?.id && branchesOptions.find(item => item.id === userInfo?.branch?.id),
      department: userInfo?.department?.id && userInfo?.department
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      const data = {
        ...values,
        roleId: values.role.id,
        branch_id: values?.branch?.id || null,
        department_id: values?.branch?.departments.find(item => item?.id === values?.department?.id)?.id || null
      };
      delete data.department;
      delete data.branch;

      if (userAvatar !== userInfo.avatar) {
        let formData = new FormData();
        formData.append('uploaded_file', userAvatar);
        dispatch(uploadUserLogo(userInfo.id, formData));
      }

      dispatch(updateUser(userInfo.id, data, !isAbleToUpdateRole && values.role?.id !== Number(userInfo.role?.id) ? { roleId: values.role?.id } : null, true));
      setSleep(true);
      setTimeout(() => {
        dispatch(getCurrentUser());
        history.push(ROUTES.PROFILE);
      }, 1000);
    }
  });

  return (
    <>
      {fetching || sleep ? (
        <Box
          sx={{
            marginTop: '150px',
            textAlign: 'center'
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1" data-testid="Title">
            Редактировать профиль
          </Typography>
          <Breadcrumbs mb="24px" aria-label="breadcrumb" data-testid="Breadcrumbs">
            <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.PROFILE}>
              Профиль
            </Link>
            <Typography color="text.secondary">Редактировать профиль</Typography>
          </Breadcrumbs>
          <Paper
            sx={{
              padding: '24px'
            }}>
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '24px'
                }}>
                <Box sx={{ display: 'flex', marginBottom: '24px' }}>
                  <Box
                    sx={{
                      marginRight: '24px'
                    }}>
                    <ImageUploader
                      name="avatar"
                      avatar={userInfo.avatar}
                      setFieldValueWithoutForm={setUserAvatar}
                      setFieldValue={setFieldValue}
                    />
                  </Box>

                  <Stack spacing={2}>
                    <CustomInput
                      name="lastName"
                      value={values.lastName}
                      placeholder="Фамилия"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      error={errors['lastName']}
                      helperText={errors['lastName']}
                    />
                    <CustomInput
                      name="firstName"
                      value={values.firstName}
                      placeholder="Имя"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      error={errors['firstName']}
                      helperText={errors['firstName']}
                    />
                    <CustomInput
                      name="middleName"
                      value={values.middleName}
                      placeholder="Отчество"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      error={errors['middleName']}
                      helperText={errors['middleName']}
                    />
                  </Stack>
                </Box>
                <Grid container spacing={2} mb="24px">
                  <Grid item>
                    <CustomInputWithMask
                      name="phone"
                      value={values.phone}
                      placeholder="Номер телефона"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item>
                    <CustomInput
                      name="email"
                      value={values.email}
                      placeholder="Email"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item>
                    <CustomSelect
                      name="role"
                      value={values.role}
                      placeholder="Роль"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      setFieldValue={setFieldValue}
                      loading={fetchingRoleOptions}
                      options={roleOptions}
                      disabled={isAbleToUpdateRole}
                      tooltip={isAbleToUpdateRole ? DISABLED_CONTROL_TOOLTIP : null}
                    />
                  </Grid>
                  <Grid item>
                    <CustomSelect
                      name="branch"
                      value={values.branch}
                      placeholder="Филиал"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      options={branchesOptions}
                      loading={fetchingBranchesOptions}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                  <Grid item>
                    <CustomSelect
                      name="department"
                      placeholder="Департамент"
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ width: 240 }}
                      size="small"
                      value={values.branch?.departments.find(item => item.id === values?.department?.id)}
                      options={values.branch?.departments}
                      setFieldValue={setFieldValue}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                <Box>
                  <CustomButton
                    component={RouterLink}
                    to={ROUTES.PROFILE}
                    color="secondary"
                    variant="contained"
                    sx={{ marginRight: '16px' }}>
                    Отмена
                  </CustomButton>
                </Box>
                <CustomButton type="submit" variant="contained" disabled={!dirty || !isValid}>
                  Сохранить
                </CustomButton>
              </Box>
            </form>
          </Paper>
        </>
      )}
    </>
  );
};
